// src/components/GeneratedSalesOrder/Footer/index.jsx
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box, Button, Grid, Typography, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, Tooltip, IconButton,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import styles from './index.module.css';
import { fromCents } from '../../../utils/totals';
import {
  buildSalesOrderFooterTaxBreakdown,
  labelSalesOrderFooterTaxRow,
} from '../salesOrderFooterTaxBreakdown';
import { fetchInvoicesListByOrder, fetchOrderBalanceStatus, fetchPublicInvoicePaymentContext } from '../../../services/invoices';
import { normalizeAvatarUrl } from '../../../utils/avatar';
import {
  buildClientApprovalJourney,
  labelClientApprovalJourneyParts,
} from '../clientApprovalJourney';

const TEXT_GREY = '#55656d';

// ---- helpers ----
const formatPhone = (p) => {
  if (!p) return '';
  const d = String(p).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return `+1 (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  return p;
};
const toName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.name || '';

const isTruthy = (v, fallback = false) => {
  if (v === true || v === 'true' || v === 1 || v === '1') return true;
  if (v === false || v === 'false' || v === 0 || v === '0') return false;
  return fallback;
};

export const resolveGeneratedDocumentRepPhoto = (rep) => {
  const url = normalizeAvatarUrl(
    rep?.profilePicture || rep?.profile_picture || rep?.avatarUrl || rep?.avatar || rep?.photoUrl || null
  );
  if (!url || /(?:^|\/)elite-core-placeholder-avatar\.png(?:[?#].*)?$/i.test(url)) return null;
  return url;
};

export const pickOrderRep = (details) => {
  const d = (details && typeof details === 'object') ? details : {};

  const normalizeUser = (u) => {
    if (!u) return null;
    return {
      name: toName(u),
      email: u?.email || '',
      phone: u?.phone_number || u?.phone || '',
      profilePicture: u?.profilePicture || u?.profile_picture || u?.avatarUrl || u?.avatar || u?.photoUrl || null,
    };
  };

  // Priority 1: explicit order rep on the order payload
  if (d.orderRep) {
    return normalizeUser(d.orderRep);
  }

  // Priority 2: explicit client rep on the order payload
  if (d.clientRep) {
    return normalizeUser(d.clientRep);
  }

  // Priority 3: assigned users by role preference
  const pools = [d.assignedUsers, d.AssignedUsers, d.assigned_to].filter(Array.isArray);
  const rolePreference = ['order-rep', 'client-rep', 'administrator'];

  for (const arr of pools) {
    for (const role of rolePreference) {
      const rep = arr.find((u) => String(u?.role || '').toLowerCase() === role);
      if (rep) return normalizeUser(rep);
    }
  }

  return null;
};

// Try to find a usable checkout link from multiple shapes
const normalizePaymentStatusLabel = (status) => {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return '';
  if (['paid', 'completed', 'succeeded', 'success'].includes(s)) return 'Paid';
  if (['open', 'pending', 'required', 'requires_payment', 'payment_required', 'unpaid'].includes(s)) return 'Open';
  if (['not_required', 'n/a', 'na'].includes(s)) return 'Not Required';
  if (['failed', 'canceled', 'cancelled', 'void'].includes(s)) return 'Failed';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const isPaidStatus = (status) => {
  const s = String(status || '').trim().toLowerCase();
  return ['paid', 'completed', 'succeeded', 'success'].includes(s);
};

const firstPositiveAmount = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const normalizePaymentMethod = (value) => String(value || '').trim().toLowerCase();

const isCustomerSelectionPayment = (value) => {
  const normalized = normalizePaymentMethod(value);
  return normalized === 'customer_selection' || normalized === 'customer-selection';
};

const sniffCheckoutLink = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  const candidates = [
    obj.checkoutLink,
    obj.checkoutUrl,
    obj.paymentLink,
    obj.hostedInvoiceUrl,
    obj.hosted_invoice_url,
    obj.stripeCheckoutUrl,
    obj.stripe_checkout_url,
    obj?.payment?.checkoutUrl,
    obj?.payment?.link,
    obj?.checkout?.url,
  ].filter(Boolean);
  const first = candidates.find((v) => typeof v === 'string' && /^https?:\/\//i.test(v));
  return first || null;
};
const deriveDisplayNameFromEmail = (email = '') => {
  const prefix = String(email || '').split('@')[0].trim();
  return prefix || '';
};

export const getSalesOrderApprovedByStorageKey = ({ estimationId = null, orderId = null } = {}) =>
  `so-approved-by-${estimationId || orderId || 'unknown'}`;

const deriveApprovedBy = (details = {}) => {
  const agreedAt = details?.agreedAt || details?.customerAgreedAt || details?.updatedAt || details?.createdAt || null;

  const fromTopLevel = {
    name: details?.agreedByName || details?.approvedByName || '',
    email: details?.agreedByEmail || details?.approvedByEmail || '',
    at: agreedAt,
  };

  // Only trust top-level identity if we actually have a name/email.
  // Avoid overriding a previously stamped Stripe payer with "Approved by —".
  if (fromTopLevel.name || fromTopLevel.email) {
    return fromTopLevel;
  }

  const audit = Array.isArray(details?.agreementAudit) ? details.agreementAudit : [];
  const approveEvent = [...audit].reverse().find((entry) =>
    String(entry?.type || '').toLowerCase().includes('agree')
  );

  if (!approveEvent) {
    return { name: '', email: '', at: agreedAt };
  }

  const agreedBy = approveEvent?.agreedBy || {};
  const actor = approveEvent?.actor || {};
  const name =
    agreedBy?.name ||
    agreedBy?.fullName ||
    actor?.name ||
    actor?.fullName ||
    '';
  const email =
    agreedBy?.email ||
    actor?.email ||
    '';

  return {
    name,
    email,
    at: approveEvent?.at || agreedAt,
  };
};

const getRoleFromAccessToken = () => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return '';
    const parts = token.split('.');
    if (parts.length < 2) return '';
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = decodeURIComponent(
      atob(padded).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join('')
    );
    const payload = JSON.parse(decoded || '{}');
    return payload?.role || '';
  } catch (_) {
    return '';
  }
};


const formatAddressLines = (address) => {
  if (!address) return [];
  const line1 = address.addressLine1 || '';
  const line2 = address.addressLine2 || '';
  const city = address.city || '';
  const state = address.stateProvince || '';
  const postal = address.postalCode || '';
  const country = address.country || '';
  const cityLine = [city, state].filter(Boolean).join(', ');
  const postalLine = [postal, country].filter(Boolean).join(' ');
  return [line1, line2, cityLine, postalLine].filter(Boolean);
};

const getDefaultViewerName = (details = {}) =>
  details?.viewerName ||
  [details?.contact?.firstName, details?.contact?.lastName].filter(Boolean).join(' ');

const getDefaultViewerEmail = (details = {}) =>
  details?.viewerEmail || details?.Email || details?.clientEmail || '';

const Footer = ({
  estimationDetails = {},
  grandTotal,
  onApprove,
  onRequestChange,
  footerAddress,
  termsHtml,
  isEstimateView = false,
  isRenderMode = false,
  artworkProofGate = null,
  artworkProofRows = [],
  hasArtworkProofingAttached = false,
  onScrollToArtworkProof,
  completeArtworkProofPackagePanel = null,
  canResetApproval = false,
  onResetApproval,
  resettingApproval = false,
  // legacy fallback
  handleAgree,
  totalsCents
}) => {
  const orderId = estimationDetails?.orderId || estimationDetails?.OrderId || estimationDetails?.id;
  const estimationId = estimationDetails?.estimationId || estimationDetails?.id || null;
  const approvedByStorageKey = getSalesOrderApprovedByStorageKey({ estimationId, orderId });
  const [approvedBy, setApprovedBy] = useState({ name: '', email: '', at: null });

  // ---- totals (grandTotal already includes tax) ----
  const taxRate = Number(estimationDetails?.tax?.rate) > 0 ? Number(estimationDetails.tax.rate) : 0;
  let subtotal, totalOrderValue;
  if (totalsCents) {
    subtotal        = fromCents(totalsCents.subtotalCents);
    totalOrderValue = fromCents(totalsCents.totalCents);
  } else {
    const safeGrand = Number.isFinite(Number(grandTotal)) ? Number(grandTotal) : 0;
    subtotal        = taxRate > 0 ? safeGrand / (1 + taxRate) : safeGrand;
    totalOrderValue = safeGrand;
  }
  const fmt = (n) => '$' + (Number(n) || 0).toFixed(2);
  const salesOrderTaxRows = useMemo(
    () => isEstimateView ? [] : buildSalesOrderFooterTaxBreakdown(estimationDetails, totalsCents),
    [isEstimateView, estimationDetails, totalsCents]
  );
  const footerTaxCents = totalsCents
    ? totalsCents.taxCents
    : Math.round((totalOrderValue - subtotal) * 100);

  const countryPart = estimationDetails?.tax?.countryCode ? `(${estimationDetails.tax.countryCode}` : '';
  const statePart   = estimationDetails?.tax?.stateCode ? `${countryPart ? ' / ' : '('}${estimationDetails.tax.stateCode}) ` : (countryPart ? ') ' : '');
  const ratePart    = `${(taxRate * 100).toFixed(2)}%`;

  const rep = pickOrderRep(estimationDetails);
  const repPhotoUrl = resolveGeneratedDocumentRepPhoto(rep);
  const [repPhotoFailed, setRepPhotoFailed] = useState(false);
  useEffect(() => setRepPhotoFailed(false), [repPhotoUrl]);
  const visibleRepPhotoUrl = repPhotoFailed ? null : repPhotoUrl;
  const companyName =
    footerAddress?.companyName || 'Elite Promotional Marketing (Elite Sportswear & Awards Ltd)';
  const addressLines = formatAddressLines(footerAddress);

  // ---- identity state ----
  const [viewerName, setViewerName] = useState(
    getDefaultViewerName(estimationDetails)
  );
  const [viewerEmail, setViewerEmail] = useState(
    getDefaultViewerEmail(estimationDetails)
  );
  const [identityAttempted, setIdentityAttempted] = useState(false);

  // simple email pattern (good-enough client-side)
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const identityOkName  = Boolean((viewerName || '').trim());
  const identityOkEmail = emailPattern.test((viewerEmail || '').trim());
  const identityOk = identityOkName && identityOkEmail;

  const [rcOpen, setRcOpen] = useState(false);
  const [rcComment, setRcComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // optimistic banners
  const [changeNoted, setChangeNoted] = useState(false);
  const [approvedNow, setApprovedNow] = useState(false);

  // ---- payment info (via invoices service) ----
  const [depositInfo, setDepositInfo] = useState(null);
  const [balanceStatus, setBalanceStatus] = useState(null);
  const [balanceInvoiceInfo, setBalanceInvoiceInfo] = useState(null);
  const [renewingLink, setRenewingLink] = useState(false);
  const [renewError, setRenewError] = useState('');
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);
  const [createPaymentLinkError, setCreatePaymentLinkError] = useState('');

  const status = String(estimationDetails?.status ?? '');
  const baseStatus = status.split('_')[0];
  const agreed = String(estimationDetails?.customerAgreed) === '1';
  const isApprovedWorkflowStatus = ['3', '6', '7', '8', '9', '10'].includes(baseStatus);
  const isApproved = isApprovedWorkflowStatus && agreed;
  const isChangeRequested = status === '4';
  const isAgreement = baseStatus === '2';
  const normalizedRole = String(getRoleFromAccessToken() || localStorage.getItem('userRole') || '').toLowerCase();
  const canViewDebugPayload = normalizedRole === 'administrator';
  const debugPayload = useMemo(() => {
    try {
      return JSON.stringify(estimationDetails || {}, null, 2);
    } catch (_) {
      return '{}';
    }
  }, [estimationDetails]);
  const debugMath = useMemo(() => {
    const products = Array.isArray(estimationDetails?.products) ? estimationDetails.products : [];
    const otherServices = Array.isArray(estimationDetails?.otherService) ? estimationDetails.otherService : [];
    const toNum = (v) => Number(v ?? 0) || 0;
    const productTotals = products.map((p, idx) => ({
      label: `product_${idx + 1}:${p?.Name || p?.name || p?.Id || 'unknown'}`,
      amount: toNum(p?.total),
    }));
    const otherRetail = otherServices.map((s, idx) => ({
      label: `other_${idx + 1}:${s?.Title || s?.title || s?.Type || 'other'}`,
      amount: toNum(s?.RetailPrice),
    }));
    const subtotalPieces = [...productTotals, ...otherRetail];
    const subtotalRaw = subtotalPieces.reduce((sum, p) => sum + p.amount, 0);
    const subtotalExpr = subtotalPieces.map((p) => p.amount.toFixed(2)).join(' + ') || '0.00';
    const taxRateNum = Number(estimationDetails?.tax?.rate || 0) || 0;
    const taxRaw = !isEstimateView && totalsCents
      ? fromCents(totalsCents.taxCents)
      : subtotalRaw * taxRateNum;
    const totalRaw = isEstimateView ? subtotalRaw + taxRaw : totalOrderValue;
    const taxGroupLines = salesOrderTaxRows.map((row) =>
      `${labelSalesOrderFooterTaxRow(row)} = ${fromCents(row.taxCents).toFixed(2)}`
    );
    return {
      subtotalExpr,
      subtotalRaw,
      taxRateNum,
      taxRaw,
      totalRaw,
      subtotalPieces,
      taxGroupLines,
    };
  }, [estimationDetails, subtotal, totalOrderValue, totalsCents, isEstimateView, salesOrderTaxRows]);

  // derive payment flags from estimationDetails
  const needsUpfront = isTruthy(estimationDetails?.requireUpfrontPayment, false);
  const dueOnSign = isTruthy(estimationDetails?.paymentDueOnSign, true); // default true
  const orderFirstApprovalFlow = estimationDetails?.clientApprovalFlow === 'order_first';

  // snapshot / hints
  const depositAmountSnap = estimationDetails?.depositAmountSnapshot;
  const currencySnap = estimationDetails?.currencySnapshot || estimationDetails?.currency;
  const paymentStatusSnap = String(estimationDetails?.paymentStatus || '').toLowerCase();
  const hintedPaid = ['paid', 'completed', 'succeeded', 'success'].includes(paymentStatusSnap);

  // reflect backend status into local banners
  useEffect(() => {
    setChangeNoted(isChangeRequested);
  }, [isChangeRequested]);

  useEffect(() => {
    setApprovedNow(isApproved);
    if (!isApproved) {
      setApprovedBy({ name: '', email: '', at: null });
      //setViewerName(getDefaultViewerName(estimationDetails));
      //setViewerEmail(getDefaultViewerEmail(estimationDetails));

      setViewerName('');
      setViewerEmail('');

      if (typeof window !== 'undefined' && approvedByStorageKey) {
        try {
          window.sessionStorage.removeItem(approvedByStorageKey);
        } catch (_) {}
      }
    }
  }, [isApproved, approvedByStorageKey, estimationDetails]);

  useEffect(() => {
    if (!isApproved) return;
    if (typeof window === 'undefined' || !approvedByStorageKey) return;
    try {
      const cached = window.sessionStorage.getItem(approvedByStorageKey);
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (!parsed || typeof parsed !== 'object') return;
      if (!parsed.name && !parsed.email) return;
      setApprovedBy((prev) => {
        if (prev?.name || prev?.email) return prev;
        return {
          name: parsed.name || '',
          email: parsed.email || '',
          at: parsed.at || null,
        };
      });
    } catch (_) {}
  }, [approvedByStorageKey, isApproved]);

  useEffect(() => {
    if (!isApproved) return;
    const derived = deriveApprovedBy(estimationDetails);
    setApprovedBy((prev) => {
      const prevHasIdentity = Boolean(prev?.name || prev?.email);
      const derivedHasIdentity = Boolean(derived?.name || derived?.email);

      // Preserve prior stamped identity (e.g., Stripe payer) when a later refetch
      // only has timestamp fields and no actor identity.
      if (prevHasIdentity && !derivedHasIdentity) {
        return {
          ...prev,
          at: prev?.at || derived?.at || null,
        };
      }
      return derived;
    });
  }, [estimationDetails, isApproved]);

  useEffect(() => {
    if (!isApproved) return;
    const derived = deriveApprovedBy(estimationDetails);
    if (!String(viewerName || '').trim() && String(derived?.name || '').trim()) {
      setViewerName(String(derived.name).trim());
    }
    if (!String(viewerEmail || '').trim() && String(derived?.email || '').trim()) {
      setViewerEmail(String(derived.email).trim());
    }
    // intentionally includes local viewer state so we only backfill empty fields
  }, [estimationDetails, viewerName, viewerEmail, isApproved]);

  useEffect(() => {
    if (typeof window === 'undefined' || !approvedByStorageKey) return;
    if (!isApproved) {
      try {
        window.sessionStorage.removeItem(approvedByStorageKey);
      } catch (_) {}
      return;
    }
    if (!(approvedBy?.name || approvedBy?.email)) return;
    try {
      window.sessionStorage.setItem(approvedByStorageKey, JSON.stringify(approvedBy));
    } catch (_) {}
  }, [approvedBy, approvedByStorageKey, isApproved]);

  const hydrateBalanceStatus = async () => {
    if (!orderId) return null;
    try {
      const next = await fetchOrderBalanceStatus(orderId);
      setBalanceStatus(next || null);
      return next || null;
    } catch (e) {
      console.warn('fetchOrderBalanceStatus error', e?.message);
      return null;
    }
  };

  const hydratePublicInvoicePaymentContext = async () => {
    const urlPaymentToken = (() => {
      try {
        return String(new URLSearchParams(window.location.search || '').get('prt') || '').trim();
      } catch (_) {
        return '';
      }
    })();
    const context = await fetchPublicInvoicePaymentContext({
      contextType: 'generated-sales-order',
      salesOrderId: estimationId,
      orderId,
      paymentToken: urlPaymentToken || estimationDetails?.depositPaymentToken || estimationDetails?.paymentToken || '',
    });
    const invoice = context?.invoice || context?.paymentContext || context || null;
    if (!invoice?.id) return false;

    setDepositInfo({
      id: invoice.id,
      amount: firstPositiveAmount(invoice.amount, invoice.amountDue, depositAmountSnap) ?? null,
      currency: String(invoice.currency || currencySnap || estimationDetails.currency || 'USD').toUpperCase(),
      status: String(invoice.paymentStatus || invoice.invoiceStatus || paymentStatusSnap || ''),
      link: sniffCheckoutLink(invoice),
      checkoutSessionId: null,
      paymentMethod: invoice.paymentMethod || invoice.invoiceMode || null,
      invoiceMode: invoice.invoiceMode || invoice.paymentMethod || null,
      paymentToken: invoice.paymentToken || invoice.publicPaymentToken || urlPaymentToken || estimationDetails?.depositPaymentToken || null,
      paymentClassification: invoice.paymentClassification || invoice.type || invoice.role || null,
    });
    return true;
  };

  // Fetch deposit info using invoices service
  const fetchDepositInfo = async () => {
    try {
      if (!orderId) return;

      const hasAccessToken = Boolean(localStorage.getItem('accessToken'));
      let invoices = [];
      if (hasAccessToken) {
        const res = await fetchInvoicesListByOrder(orderId);
        invoices = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      } else {
        const loadedPublicContext = await hydratePublicInvoicePaymentContext();
        if (loadedPublicContext) {
          await hydrateBalanceStatus();
          return;
        }
      }

      const parseRel = (r) => {
        if (!r) return {};
        if (typeof r === 'string') {
          try { return JSON.parse(r); } catch { return {}; }
        }
        return r;
      };

      const isDepositInv = (inv) => {
        const rel = parseRel(inv?.invoiceRelationships);
        const role = String(rel.role || rel.type || '').toLowerCase();
        const type = String(rel.type || '').toLowerCase();
        const classification = String(rel.paymentClassification || '').toLowerCase();
        return (
          ['deposit', 'deposit-invoice', 'deposit_request', 'depositrequest'].includes(role)
          || type === 'full_upfront'
          || classification === 'full_upfront'
        );
      };

      const isBalanceInv = (inv) => {
        const rel = parseRel(inv?.invoiceRelationships);
        const role = String(rel.role || rel.type || '').toLowerCase();
        return role === 'balance';
      };

      const isDisplayable = (inv) => {
        const s = String(inv?.invoiceStatus || inv?.paymentStatus || '').toLowerCase();
        return !['void', 'canceled', 'cancelled'].includes(s);
      };

      const pickInvoiceAmount = (inv) => {
        const rel = parseRel(inv?.invoiceRelationships);
        const accountingRef = parseRel(inv?.accountingRef);
        const invoiceHistory = accountingRef?.thirdParty?.invoiceHistory || {};
        const role = String(rel?.role || rel?.type || '').toLowerCase();
        const isAmountBasedRole = ['deposit', 'balance', 'partial', 'progress'].includes(role);

        if (role === 'balance') {
          return firstPositiveAmount(
            rel?.currentInvoiceAmount,
            rel?.remainingBalance,
            rel?.activeInvoiceTotal,
            Number(rel?.balanceClassification?.balanceCents || 0) / 100,
            Number(rel?.expectedTotalCents || 0) / 100,
            Number(invoiceHistory?.currentInvoiceAmountCents || 0) / 100,
            Number(invoiceHistory?.expectedTotalCents || 0) / 100,
            inv?.amount,
            inv?.amountDue
          );
        }

        if (isAmountBasedRole) {
          return firstPositiveAmount(
            inv?.amount,
            rel?.currentInvoiceAmount,
            Number(invoiceHistory?.currentInvoiceAmountCents || 0) / 100,
            inv?.amountDue
          );
        }

        return firstPositiveAmount(inv?.amount, inv?.amountDue, inv?.total, inv?.totalAmount, inv?.grandTotal);
      };

      const toInvoiceRank = (inv) => {
        const idNum = Number(inv?.id);
        const ts = new Date(inv?.updatedAt || inv?.createdAt || 0).getTime();
        return {
          idNum: Number.isFinite(idNum) ? idNum : 0,
          ts: Number.isFinite(ts) ? ts : 0,
        };
      };

      const parseInvoiceRel = (inv) => parseRel(inv?.invoiceRelationships);

      const balanceInv = [...(invoices || [])]
        .filter(inv => isBalanceInv(inv) && isDisplayable(inv))
        .sort((a, b) => {
          const aOpen = !(isPaidStatus(a?.paymentStatus) || isPaidStatus(a?.invoiceStatus)) ? 1 : 0;
          const bOpen = !(isPaidStatus(b?.paymentStatus) || isPaidStatus(b?.invoiceStatus)) ? 1 : 0;
          if (bOpen !== aOpen) return bOpen - aOpen;

          const aActive = a?.isActive !== false ? 1 : 0;
          const bActive = b?.isActive !== false ? 1 : 0;
          if (bActive !== aActive) return bActive - aActive;

          const ra = toInvoiceRank(a);
          const rb = toInvoiceRank(b);
          if (rb.idNum !== ra.idNum) return rb.idNum - ra.idNum;
          return rb.ts - ra.ts;
        })[0] || null;

      if (balanceInv) {
        setBalanceInvoiceInfo({
          id: balanceInv.id,
          amount: pickInvoiceAmount(balanceInv),
          currency: String(balanceInv.currency || currencySnap || estimationDetails.currency || 'USD').toUpperCase(),
          status: String(balanceInv.paymentStatus || balanceInv.invoiceStatus || ''),
          link: sniffCheckoutLink(balanceInv),
          checkoutSessionId: balanceInv.checkoutSessionId || null,
          paymentMethod: balanceInv.paymentMethod || balanceInv.invoiceMode || null,
          invoiceMode: balanceInv.invoiceMode || balanceInv.paymentMethod || null,
        });
      } else {
        setBalanceInvoiceInfo(null);
      }

      await hydrateBalanceStatus();

      const dep = [...(invoices || [])]
        .filter(inv => isDepositInv(inv) && isDisplayable(inv))
        .sort((a, b) => {
          const aRel = parseInvoiceRel(a);
          const bRel = parseInvoiceRel(b);

          const aExactInvoice = estimationDetails?.depositInvoiceId && Number(a?.id) === Number(estimationDetails?.depositInvoiceId) ? 1 : 0;
          const bExactInvoice = estimationDetails?.depositInvoiceId && Number(b?.id) === Number(estimationDetails?.depositInvoiceId) ? 1 : 0;
          if (bExactInvoice !== aExactInvoice) return bExactInvoice - aExactInvoice;

          const aExactSalesOrder = estimationId && Number(aRel?.salesOrderId) === Number(estimationId) ? 1 : 0;
          const bExactSalesOrder = estimationId && Number(bRel?.salesOrderId) === Number(estimationId) ? 1 : 0;
          if (bExactSalesOrder !== aExactSalesOrder) return bExactSalesOrder - aExactSalesOrder;

          const aPaid = isPaidStatus(a?.paymentStatus) || isPaidStatus(a?.invoiceStatus) ? 1 : 0;
          const bPaid = isPaidStatus(b?.paymentStatus) || isPaidStatus(b?.invoiceStatus) ? 1 : 0;
          if (bPaid !== aPaid) return bPaid - aPaid;

          const ra = toInvoiceRank(a);
          const rb = toInvoiceRank(b);
          if (rb.idNum !== ra.idNum) return rb.idNum - ra.idNum;
          return rb.ts - ra.ts;
        })[0] || null;

      if (dep) {
        const resolvedAmount = pickInvoiceAmount(dep);
        setDepositInfo({
          id: dep.id,
          amount: resolvedAmount ?? depositAmountSnap ?? null,
          currency: String(dep.currency || currencySnap || estimationDetails.currency || 'USD').toUpperCase(),
          status: String(dep.paymentStatus || dep.invoiceStatus || paymentStatusSnap || ''),
          link: sniffCheckoutLink(dep),
          checkoutSessionId: dep.checkoutSessionId || null,
          paymentMethod: dep.paymentMethod || dep.invoiceMode || null,
          invoiceMode: dep.invoiceMode || dep.paymentMethod || null,
          paymentToken: dep.paymentToken || dep.publicPaymentToken || estimationDetails?.depositPaymentToken || null,
          paymentClassification: (() => {
            const rel = parseInvoiceRel(dep);
            return rel?.paymentClassification || rel?.type || rel?.role || null;
          })(),
        });
        return;
      }

      const loadedPublicContext = await hydratePublicInvoicePaymentContext().catch((err) => {
        console.warn('fetchPublicInvoicePaymentContext error', err?.message);
        return false;
      });
      if (loadedPublicContext) return;

      // fallback to snapshot if invoice list hasn't caught up
      if (estimationDetails?.depositInvoiceId || depositAmountSnap) {
        setDepositInfo({
          id: estimationDetails?.depositInvoiceId || null,
          amount: depositAmountSnap || null,
          currency: String(currencySnap || estimationDetails.currency || 'USD').toUpperCase(),
          status: paymentStatusSnap || '',
          link: sniffCheckoutLink(estimationDetails),
          checkoutSessionId: null,
          paymentMethod: estimationDetails?.paymentMethod || estimationDetails?.invoiceMode || null,
          invoiceMode: estimationDetails?.invoiceMode || estimationDetails?.paymentMethod || null,
          paymentToken: estimationDetails?.depositPaymentToken || estimationDetails?.paymentToken || null,
          paymentClassification: estimationDetails?.upfrontMode === 'full' ? 'full_upfront' : 'deposit',
        });
      } else {
        setDepositInfo(null);
      }
    } catch (e) {
      console.warn('fetchDepositInfo error', e?.message);
      setDepositInfo(null);
      setBalanceInvoiceInfo(null);
    }
  };

  // Seed deposit info when Agreement + upfront + collect-on-sign
  useEffect(() => {
    if (isRenderMode) return;
    if (!(isAgreement || isApproved || approvedNow) || !needsUpfront || !dueOnSign) return;
    fetchDepositInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRenderMode,
    isAgreement,
    isApproved,
    approvedNow,
    needsUpfront,
    dueOnSign,
    orderId,
    estimationDetails?.depositInvoiceId,
    estimationDetails?.depositAmountSnapshot,
    estimationDetails?.updatedAt,
  ]);

  const canShowActions = !isRenderMode && !isEstimateView && Number(baseStatus) > 1;
  const normalizedArtworkProofGate = useMemo(() => {
    const total = Number(artworkProofGate?.total || 0);
    return {
      total,
      approved: Number(artworkProofGate?.approved || 0),
      rejected: Number(artworkProofGate?.rejected || 0),
      pending: Number(artworkProofGate?.pending || 0),
      canProceed: artworkProofGate?.canProceed !== false,
      canSign: artworkProofGate?.canSign !== false,
      blockingReason: artworkProofGate?.blockingReason || null,
      firstBlockingMappingId: artworkProofGate?.firstBlockingMappingId ?? null,
      firstBlockingProofId: artworkProofGate?.firstBlockingProofId ?? null,
      isActive: artworkProofGate?.isActive === true && total > 0,
    };
  }, [artworkProofGate]);
  const resolvedHasArtworkProofingAttached =
    hasArtworkProofingAttached === true || Number(normalizedArtworkProofGate.total || 0) > 0;
  const artworkProofGateBlocks =
    normalizedArtworkProofGate.isActive && !normalizedArtworkProofGate.canProceed;
  const artworkProofGateAllowsProceeding =
    !normalizedArtworkProofGate.isActive || normalizedArtworkProofGate.canProceed;
  const effectiveArtworkProofGateBlocks =
    !orderFirstApprovalFlow && artworkProofGateBlocks;
  const effectiveArtworkProofGateAllowsProceeding =
    orderFirstApprovalFlow || artworkProofGateAllowsProceeding;
  const artworkProofGateMessage = normalizedArtworkProofGate.blockingReason === 'rejected'
    ? 'One or more artwork proofs require changes before this sales order can be approved.'
    : 'Artwork proofs must be approved before approving this sales order.';
  const artworkProofGateSummaryText = normalizedArtworkProofGate.total > 0
    ? normalizedArtworkProofGate.pending > 0
      ? `Artwork proofs: ${normalizedArtworkProofGate.approved} approved, ${normalizedArtworkProofGate.pending} pending`
      : `${normalizedArtworkProofGate.approved} of ${normalizedArtworkProofGate.total} artwork proofs approved`
    : '';
  const identityMissing = useMemo(() => !viewerEmail && !viewerName, [viewerEmail, viewerName]);
  const snapshotRevisionFromUrl = (() => {
    if (typeof window === 'undefined') return null;
    try {
      return new URLSearchParams(window.location.search || '').get('snapshotRevision');
    } catch (_) {
      return null;
    }
  })();
  const hasSnapshotRevisionQuery = Boolean(snapshotRevisionFromUrl);
  const resolvedLifecycle = String(
    estimationDetails?.resolvedLifecycleStatus ||
    estimationDetails?.renderedSnapshotLifecycleStatus ||
    ''
  ).toLowerCase();
  const isActiveSnapshotView = !hasSnapshotRevisionQuery || resolvedLifecycle === 'active';
  const isInactiveSnapshotRevisionView = hasSnapshotRevisionQuery && !isActiveSnapshotView;
  const goToLatestSnapshotVersion = () => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search || '');
      params.delete('snapshotRevision');
      params.delete('snapshotId');
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
      window.location.assign(next);
    } catch (_) {}
  };

  // Prefer new props; fallback to legacy handleAgree if present
  const approveFn = onApprove || (typeof handleAgree === 'function'
    ? async ({ agreedByName = '', agreedByEmail = '' }) => handleAgree({
        status: 2,
        customerAgreed: true,
        agreedByName,
        agreedByEmail
      })
    : null
  );

  const requestChangeFn = onRequestChange || (typeof handleAgree === 'function'
    ? async ({ comment = '', agreedByName = '', agreedByEmail = '' }) => handleAgree({
        requestChange: true,
        requestChangeComment: comment,
        agreedByName,
        agreedByEmail
      })
    : null
  );

  // gating rules
  const agreeDisabledBase =
    agreed || isApproved || approvedNow || isChangeRequested || changeNoted || submitting;

  const mustPayFirst = !orderFirstApprovalFlow && isAgreement && needsUpfront && dueOnSign;
  const statusRaw = String(depositInfo?.status || paymentStatusSnap || '');
  const statusLower = statusRaw.toLowerCase();
  const statusLabel = normalizePaymentStatusLabel(statusRaw) || 'Open';
  const isPaid = ['paid', 'succeeded', 'completed', 'success'].includes(statusLower) || hintedPaid;
  const isLinkExpired = ['expired', 'unpaid', 'canceled', 'cancelled'].includes(statusLower);
  const paymentMethodRaw =
    depositInfo?.paymentMethod ||
    depositInfo?.invoiceMode ||
    estimationDetails?.paymentMethod ||
    estimationDetails?.invoiceMode ||
    '';
  const isCustomerSelectionDeposit = isCustomerSelectionPayment(paymentMethodRaw);
  const paymentClassification = String(
    depositInfo?.paymentClassification ||
    (estimationDetails?.upfrontMode === 'full' ? 'full_upfront' : estimationDetails?.upfrontMode === 'deposit' ? 'deposit' : '')
  ).toLowerCase();
  const isFullUpfrontPayment = paymentClassification === 'full_upfront' || String(estimationDetails?.upfrontMode || '').toLowerCase() === 'full';
  const paymentRequiredLabel = isFullUpfrontPayment ? 'Payment required' : 'Deposit required';
  const amountDueLabel = isFullUpfrontPayment ? 'Amount due' : 'Deposit due';
  const paidAmountLabel = isFullUpfrontPayment ? 'Amount paid' : 'Amount paid';
  const payNowLabel = isFullUpfrontPayment ? 'Pay now' : 'Pay by Card';
  const approvalJourney = useMemo(
    () => labelClientApprovalJourneyParts(buildClientApprovalJourney({
      flow: estimationDetails?.clientApprovalFlow,
      hasProof: resolvedHasArtworkProofingAttached,
      requiresPayment: needsUpfront && dueOnSign,
      paymentDueOnSign: dueOnSign,
      paymentLabel: isFullUpfrontPayment ? 'Complete payment' : 'Pay your deposit',
    })),
    [
      dueOnSign,
      estimationDetails?.clientApprovalFlow,
      isFullUpfrontPayment,
      needsUpfront,
      resolvedHasArtworkProofingAttached,
    ]
  );
  const orderFirstProofComplete =
    !resolvedHasArtworkProofingAttached || normalizedArtworkProofGate.canProceed;
  const currentJourneyStepKey = !orderFirstApprovalFlow
    ? null
    : !(isApproved || approvedNow)
      ? 'sales-order'
      : (resolvedHasArtworkProofingAttached && !orderFirstProofComplete)
        ? 'proof'
        : (needsUpfront && dueOnSign && !isPaid)
          ? 'payment'
          : null;
  const currentJourneyStep = approvalJourney.find((step) => step.key === currentJourneyStepKey) || null;
  const guidedHasPostApprovalStep =
    orderFirstApprovalFlow &&
    (resolvedHasArtworkProofingAttached || (needsUpfront && dueOnSign));
  const showGuidedResetApproval =
    guidedHasPostApprovalStep &&
    canResetApproval &&
    (isApproved || approvedNow) &&
    currentJourneyStepKey !== 'sales-order';
  const hasMultipleJourneyParts = orderFirstApprovalFlow && approvalJourney.length > 1;
  const isJourneyStepComplete = (stepKey) => {
    if (stepKey === 'sales-order') return Boolean(isApproved || approvedNow);
    if (stepKey === 'proof') return Boolean(orderFirstProofComplete);
    if (stepKey === 'payment') return Boolean(isPaid);
    return false;
  };
  const getArtworkApprovalTooltip = () => {
    const rows = Array.isArray(artworkProofRows) ? artworkProofRows : [];
    const uniqueApprovers = (side) => {
      const seen = new Set();
      const labels = [];
      rows.forEach((row) => {
        const approval = row?.proof?.approvals?.[side] || {};
        if (String(approval?.state || '').toLowerCase() !== 'approved') return;
        const by = approval?.by || {};
        const label = by?.name
          ? (by?.email ? `${by.name} (${by.email})` : by.name)
          : by?.email || '';
        const normalized = String(label || '').trim().toLowerCase();
        if (!label || seen.has(normalized)) return;
        seen.add(normalized);
        labels.push(label);
      });
      return labels;
    };

    const salesApprovers = uniqueApprovers('user');
    const clientApprovers = uniqueApprovers('client');
    const salesLabel = salesApprovers.length ? salesApprovers.join(', ') : 'Completed';
    const clientLabel = clientApprovers.length ? clientApprovers.join(', ') : 'Completed';

    return `Artwork approved — Sales representative: ${salesLabel}. Client: ${clientLabel}.`;
  };

  const getJourneyStepTooltip = (step) => {
    if (!step) return '';
    if (step.key === 'sales-order' && isJourneyStepComplete('sales-order')) {
      const approver = approvedByDisplayName || approvedBy.email || 'Client';
      const email = approvedBy.email ? ` (${approvedBy.email})` : '';
      const when = approvedBy.at ? ` on ${new Date(approvedBy.at).toLocaleString()}` : '';
      return `Sales Order approved by ${approver}${email}${when}.`;
    }
    if (step.key === 'proof' && isJourneyStepComplete('proof')) {
      return getArtworkApprovalTooltip();
    }
    if (step.key === 'payment' && isJourneyStepComplete('payment')) {
      return 'Payment completed.';
    }
    if (step.key === currentJourneyStepKey) {
      return 'Current step';
    }
    return step.description || step.label || '';
  };

  // Only render approval when the active workflow allows Sales Order approval.
  const renderAgreeButton = effectiveArtworkProofGateAllowsProceeding && (!mustPayFirst || isPaid);

  // ⛔ enforce identity before either action
  const agreeDisabled =
    agreeDisabledBase || !identityOk || isInactiveSnapshotRevisionView || effectiveArtworkProofGateBlocks;
  const requestDisabled =
    (isApproved || approvedNow || isChangeRequested || changeNoted || submitting) || isInactiveSnapshotRevisionView;
  const approvedByDisplayName = approvedBy.name || deriveDisplayNameFromEmail(approvedBy.email);

  const onApproveClick = async () => {
    if (effectiveArtworkProofGateBlocks) {
      onScrollToArtworkProof?.(normalizedArtworkProofGate.firstBlockingMappingId ?? null);
      return;
    }
    if (!identityOk) {
      setIdentityAttempted(true);
      return;
    }
    try {
      setSubmitting(true);
      if (typeof approveFn !== 'function') throw new Error('Missing onApprove/handleAgree');

      const result = await approveFn({
        agreedByEmail: viewerEmail || '',
        agreedByName:  viewerName || ''
      });

      if (result?.blockedByProofGate) {
        onScrollToArtworkProof?.(result?.gate?.firstBlockingMappingId ?? normalizedArtworkProofGate.firstBlockingMappingId ?? null);
        return;
      }
      if (result?.ok === false) {
        return;
      }

      // Normal success → approved (server should move to status 3)
      setApprovedNow(true);
      setApprovedBy({
        name:  viewerName || '',
        email: viewerEmail || '',
        at:    new Date().toISOString(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onRequestChangeSend = async () => {
    if (!effectiveArtworkProofGateBlocks && !identityOk) {
      setIdentityAttempted(true);
      return;
    }
    try {
      setSubmitting(true);
      if (typeof requestChangeFn !== 'function') throw new Error('Missing onRequestChange/handleAgree');
      const result = await requestChangeFn({
        comment: rcComment,
        agreedByEmail: viewerEmail || '',
        agreedByName: viewerName || ''
      });
      if (result?.ok === false) {
        return;
      }
      setRcOpen(false);
      setRcComment('');
      setChangeNoted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const depositDisplay =
    (depositInfo?.amount ?? depositAmountSnap) != null
      ? `${String(depositInfo?.currency || currencySnap || estimationDetails?.currency || '').toUpperCase()} ${Number(depositInfo?.amount ?? depositAmountSnap).toFixed(2)}`
      : null;
  const formatCurrency = (amount, currency) =>
    `${String(currency || currencySnap || estimationDetails?.currency || 'USD').toUpperCase()} ${Number(amount || 0).toFixed(2)}`;
  const balanceInvoicePaid = isPaidStatus(balanceInvoiceInfo?.status);
  const balanceInvoiceAmount = Number(
    balanceInvoiceInfo?.amount ??
    balanceStatus?.remainingCustomerPayable ??
    balanceStatus?.uninvoicedAdditionalBalance ??
    0
  );
  const balanceInvoiceDisplay = balanceInvoiceAmount > 0
    ? formatCurrency(balanceInvoiceAmount, balanceInvoiceInfo?.currency)
    : null;
  const remainingUninvoicedDisplay = Number(balanceStatus?.uninvoicedAdditionalBalance || 0) > 0
    ? formatCurrency(balanceStatus.uninvoicedAdditionalBalance, balanceInvoiceInfo?.currency)
    : null;
  const balancePaymentStatusLabel = normalizePaymentStatusLabel(balanceInvoiceInfo?.status) || 'Open';
  const balancePaymentLink = balanceInvoiceInfo?.link || null;
  const renderBalancePaymentNote = () => {
    if (balanceStatus?.hasUninvoicedAdditionalBalance && remainingUninvoicedDisplay) {
      return (
        <Typography variant="body2" sx={{ mb: 0.5, color: '#374151' }}>
          Remaining balance to be invoiced: <strong>{remainingUninvoicedDisplay}</strong>
        </Typography>
      );
    }

    if (balanceStatus?.hasAdditionalBalanceDue && balanceInvoiceInfo && !balanceInvoicePaid && balanceInvoiceDisplay) {
      return (
        <>
          <Typography variant="body2" sx={{ mb: 0.5, color: '#374151' }}>
            Remaining balance invoice created: <strong>{balanceInvoiceDisplay}</strong>
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280' }}>
            Payment status: <strong>{balancePaymentStatusLabel}</strong>
          </Typography>
          {balancePaymentLink ? (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <a href={balancePaymentLink} target="_blank" rel="noopener noreferrer">
                View payment link
              </a>
            </Typography>
          ) : null}
        </>
      );
    }

    if (balanceInvoiceInfo && balanceInvoicePaid && balanceInvoiceDisplay) {
      return (
        <Typography variant="body2" sx={{ mb: 0.5, color: '#155724' }}>
          Remaining balance paid: <strong>{balanceInvoiceDisplay}</strong>
        </Typography>
      );
    }

    return null;
  };
  const balancePaymentNote = renderBalancePaymentNote();
  const paymentLink = depositInfo?.link || null;
  const stripeDashboardUrl = (() => {
    if (!depositInfo?.checkoutSessionId) return null;
    const isTest = String(depositInfo.checkoutSessionId).includes('_test');
    const base = isTest ? 'https://dashboard.stripe.com/test' : 'https://dashboard.stripe.com';
    return `${base}/checkout/sessions/${depositInfo.checkoutSessionId}`;
  })();

  const openPaymentLink = async () => {
    const refreshed = await refreshPaymentStatus();
    const link = refreshed?.checkoutLink || paymentLink;
    if (!link) return;
    try {
      window.open(link, '_blank', 'noopener,noreferrer');
    } catch {
      window.location.href = link;
    }
  };

  const copyPaymentLink = async () => {
    const refreshed = await refreshPaymentStatus();
    const link = refreshed?.checkoutLink || paymentLink;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch (_) {}
  };

  const refreshPaymentStatus = async () => {
    try {
      if (!depositInfo?.id) return { ok: false };
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(window.location.search || '');
      const reconcileToken = String(params.get('prt') || depositInfo?.paymentToken || estimationDetails?.depositPaymentToken || '').trim();
      const usePublicReconcile = !token && Boolean(reconcileToken);
      const endpoint = usePublicReconcile
        ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/public/reconcile?prt=${encodeURIComponent(reconcileToken)}`
        : `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/${depositInfo.id}/refresh`;

      const res = await fetch(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));

      if (['paid', 'succeeded', 'completed'].includes(String(data?.status || '').toLowerCase())) {
        setApprovedNow(true);
        if (!String(viewerName || '').trim() && data?.payer?.name) setViewerName(data.payer.name);
        if (!String(viewerEmail || '').trim() && data?.payer?.email) setViewerEmail(data.payer.email);
        setApprovedBy((prev) => ({
          name: data?.payer?.name || viewerName || prev?.name || '',
          email: data?.payer?.email || viewerEmail || prev?.email || '',
          at: prev?.at || new Date().toISOString(),
        }));
      }

      if (['paid', 'succeeded', 'completed'].includes(String(data?.status || '').toLowerCase())) {
        try {
          const params = new URLSearchParams(window.location.search || '');
          if (String(params.get('payment') || '').toLowerCase() === 'success') {
            params.delete('payment');
            params.delete('deposit');
            params.delete('prt');
            const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
            window.history.replaceState({}, '', next);
          }
        } catch (_) {}
      }

      const renewedLink = data?.checkoutLink || data?.session?.url || null;
      if (renewedLink) {
        setDepositInfo((prev) => ({ ...(prev || {}), link: renewedLink }));
      }

      // Also refresh depositInfo from invoices service
      await fetchDepositInfo();
      return { ok: true, checkoutLink: renewedLink };
    } catch (err) {
      console.warn('refreshPaymentStatus error', err);
      return { ok: false };
    }
  };


  const createPublicPaymentLink = async () => {
    if (!depositInfo?.id || creatingPaymentLink) return false;

    setCreatePaymentLinkError('');
    setCreatingPaymentLink(true);

    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams(window.location.search || '');
      const reconcileToken = String(params.get('prt') || depositInfo?.paymentToken || estimationDetails?.depositPaymentToken || '').trim();

      let endpoint = '';
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (reconcileToken) {
        endpoint = `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/public/${depositInfo.id}/payment-link`;
        options.body = JSON.stringify({ prt: reconcileToken });
      } else if (token) {
        endpoint = `${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/${depositInfo.id}/renew-checkout-link`;
        options.headers.Authorization = `Bearer ${token}`;
      } else {
        throw new Error('Unable to generate a payment link for this session. Please reopen the secure approval link or contact your sales rep.');
      }

      const res = await fetch(endpoint, options);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || 'Unable to generate payment link.');
      }

      const invoicePayload = data?.invoice || data || {};
      const link =
        invoicePayload.checkoutLink ||
        data?.checkoutLink ||
        data?.session?.url ||
        null;

      if (!link) {
        await fetchDepositInfo();
        throw new Error('Payment link was generated but not returned. Please refresh and try again.');
      }

      setDepositInfo((prev) => ({
        ...(prev || {}),
        link,
        checkoutSessionId:
          invoicePayload.checkoutSessionId ||
          data?.checkoutSessionId ||
          prev?.checkoutSessionId ||
          null,
        status:
          invoicePayload.paymentStatus ||
          data?.paymentStatus ||
          prev?.status ||
          'Pending',
      }));

      try {
        window.open(link, '_blank', 'noopener,noreferrer');
      } catch {
        window.location.href = link;
      }

      await fetchDepositInfo();
      return true;
    } catch (err) {
      setCreatePaymentLinkError(err?.message || 'Unable to generate payment link.');
      return false;
    } finally {
      setCreatingPaymentLink(false);
    }
  };



  const renderCustomerSelectionPaymentOptions = () => (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Typography variant="body2" sx={{ color: '#374151' }}>
        {orderFirstApprovalFlow
          ? (isFullUpfrontPayment
              ? 'Complete your payment securely below.'
              : 'Pay your deposit securely below.')
          : (isFullUpfrontPayment
              ? 'Choose how you would like to pay this invoice.'
              : 'Choose how you would like to pay this deposit.')}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          onClick={createPublicPaymentLink}
          disabled={creatingPaymentLink}
        >
          {creatingPaymentLink
            ? 'Generating...'
            : orderFirstApprovalFlow
              ? (isFullUpfrontPayment ? 'Pay now' : 'Pay deposit')
              : payNowLabel}
        </Button>

        <Button
          variant="text"
          size="small"
          onClick={refreshPaymentStatus}
        >
          {orderFirstApprovalFlow ? 'Refresh payment status' : 'I’ve paid — refresh'}
        </Button>
      </Stack>

      {createPaymentLinkError ? (
        <Typography variant="caption" sx={{ color: '#b91c1c' }}>
          {createPaymentLinkError}
        </Typography>
      ) : null}

      {!orderFirstApprovalFlow ? (
        <Typography variant="caption" sx={{ color: '#6b7280' }}>
          After payment succeeds, the <strong>Agree</strong> button will become available.
        </Typography>
      ) : null}
    </Box>
  );

  const renewPaymentLink = async () => {
    if (!depositInfo?.id || renewingLink) return;
    setRenewError('');
    setRenewingLink(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/invoices/${depositInfo.id}/renew-checkout-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Unable to renew payment link.');
      }

      await fetchDepositInfo();
      return true;
    } catch (err) {
      setRenewError(err?.message || 'Unable to renew payment link.');
      return false;
    } finally {
      setRenewingLink(false);
    }
  };
  useEffect(() => {
    if (isRenderMode) return;
    if (!isAgreement || !needsUpfront || !dueOnSign) return;

    const params = new URLSearchParams(window.location.search || '');
    const paymentState = String(params.get('payment') || '').toLowerCase();
    if (paymentState !== 'success') return;

    refreshPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositInfo?.id, dueOnSign, isAgreement, isRenderMode, needsUpfront]);

  return (
    <>
      <Grid container className={styles.footerContainer}>
        {/* Left: contact */}
        <Grid md={6} xs={12}>
          <Box sx={visibleRepPhotoUrl ? { display: 'flex', alignItems: 'flex-start', gap: '12px' } : { display: 'contents' }}>
            {visibleRepPhotoUrl ? (
              <img
                src={visibleRepPhotoUrl}
                alt={`${rep?.name || 'Salesperson'} profile`}
                data-testid="generated-document-rep-photo"
                onError={() => setRepPhotoFailed(true)}
                style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flex: '0 0 48px' }}
              />
            ) : null}
            <Box sx={visibleRepPhotoUrl ? undefined : { display: 'contents' }}>
              <Typography variant='h6'>Questions about your order?</Typography>

              {rep ? (
                <Box>
              <Typography color={TEXT_GREY} variant='body2'>{rep.name || 'N/A'}</Typography>

              {/* Only render anchors when we actually have links (a11y: anchor-is-valid) */}
              {rep.email ? (
                <Typography color={TEXT_GREY} variant='body2'>
                  <a href={`mailto:${rep.email}`}>{rep.email}</a>
                </Typography>
              ) : (
                <Typography color={TEXT_GREY} variant='body2' component="span">
                  N/A
                </Typography>
              )}

              {rep.phone ? (
                <Typography color={TEXT_GREY} variant='body2'>
                  <a href={`tel:${rep.phone}`}>{formatPhone(rep.phone)}</a>
                </Typography>
              ) : (
                <Typography color={TEXT_GREY} variant='body2' component="span">
                  N/A
                </Typography>
              )}
                </Box>
              ) : (
                <Box>
              <Typography color={TEXT_GREY} variant='body2'>N/A</Typography>
              <Typography color={TEXT_GREY} variant='body2' component="span">N/A</Typography>
              <Typography color={TEXT_GREY} variant='body2' component="span">N/A</Typography>
                </Box>
              )}

              <Box>
            <Typography color={TEXT_GREY} variant='body2' sx={{ fontWeight: 600 }}>
              {companyName}
            </Typography>
            {addressLines.length ? (
              addressLines.map((line) => (
                <Typography key={line} color={TEXT_GREY} variant='body2'>
                  {line}
                </Typography>
              ))
            ) : (
              <>
                <Typography color={TEXT_GREY} variant='body2'>14703-118 Ave NW</Typography>
                <Typography color={TEXT_GREY} variant='body2'>Edmonton, Alberta T5L 2M7</Typography>
                <Typography color={TEXT_GREY} variant='body2'>Canada</Typography>
              </>
            )}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Right: totals */}
        <Grid item md={6} xs={12} className={`${styles.totalPriceContainer} ${styles.orderTotals} ${isEstimateView ? styles.estimatePdfTotals : ''}`}>
          <Box className={styles.titleValue}>
            <Typography variant="body1" className={styles.title} gutterBottom>SUBTOTAL</Typography>
            <Typography variant="body1" className={styles.value}>{fmt(subtotal)}</Typography>
          </Box>
          {isEstimateView ? (
            <Box className={styles.titleValue}>
              <Typography variant="body1" className={styles.title} gutterBottom>TAX - GST</Typography>
              <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                {`${countryPart || ''}${statePart || ''}${ratePart}`}
              </Typography>
            </Box>
          ) : salesOrderTaxRows.length ? (
            salesOrderTaxRows.map((row) => (
              <Box className={styles.titleValue} key={row.key}>
                <Typography variant="body1" className={styles.title} gutterBottom>
                  {labelSalesOrderFooterTaxRow(row)}
                </Typography>
                <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                  {fmt(fromCents(row.taxCents))}
                </Typography>
              </Box>
            ))
          ) : (
            <Box className={styles.titleValue}>
              <Typography variant="body1" className={styles.title} gutterBottom>TAX</Typography>
              <Typography variant="body1" className={`${styles.value} ${styles.GSTContainer}`}>
                {fmt(fromCents(footerTaxCents))}
              </Typography>
            </Box>
          )}
          <Box className={styles.titleValue}>
            <Typography variant="body1" className={styles.title} gutterBottom>TOTAL</Typography>
            <Typography variant="body1" className={styles.value}>{fmt(totalOrderValue)}</Typography>
          </Box>
        </Grid>

        {(isApproved || approvedNow || isChangeRequested || changeNoted) && (
          <Typography className={styles.printOnly}>
            Approval status: {(isApproved || approvedNow) ? 'Approved' : 'Changes requested'}
          </Typography>
        )}

        {/* Actions */}
        {canShowActions && (
          <Box
            className={styles.printHidden}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: '100%',
              alignItems: orderFirstApprovalFlow ? 'stretch' : 'end',
              order: orderFirstApprovalFlow ? -1 : 'initial',
              ...(orderFirstApprovalFlow ? {
                mt: 1,
                mb: 3,
                p: '30px',
                border: 'none',
                borderRadius: 0,
                background: 'transparent',
                boxShadow: 'none',
              } : {}),
            }}
          >
            {showGuidedResetApproval ? (
              <Tooltip title="Reset client approval and return this Sales Order to Agreement Pending" arrow>
                <IconButton
                  aria-label="Reset client approval"
                  onClick={onResetApproval}
                  disabled={resettingApproval}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 2,
                    opacity: { xs: 1, md: 0.12 },
                    color: '#9a6700',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(154,103,0,0.18)',
                    transition: 'opacity 160ms ease, background-color 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: '#fff8e1',
                      borderColor: 'rgba(154,103,0,0.38)',
                    },
                    '.MuiBox-root:hover > &': {
                      opacity: 1,
                    },
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            {orderFirstApprovalFlow && currentJourneyStep ? (
              <Box sx={{ width: '100%', mb: 0.75 }}>
                {hasMultipleJourneyParts ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mb: 1.2, flexWrap: 'wrap', rowGap: 0.75 }}
                  >
                    {approvalJourney.map((step) => {
                      const isComplete = isJourneyStepComplete(step.key);
                      const isCurrent = step.key === currentJourneyStepKey;
                      return (
                        <Tooltip key={step.key} title={getJourneyStepTooltip(step)} arrow>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.55,
                              minHeight: 26,
                              px: 1.05,
                              borderRadius: 99,
                              border: '1px solid',
                              borderColor: isComplete
                                ? '#b7dec5'
                                : isCurrent
                                  ? '#bfd3ee'
                                  : '#dfe6ef',
                              backgroundColor: isComplete
                                ? '#eef8f1'
                                : isCurrent
                                  ? '#eef4fb'
                                  : '#f8fafc',
                              color: isComplete
                                ? '#287447'
                                : isCurrent
                                  ? '#46627f'
                                  : '#7a8798',
                              cursor: 'default',
                              transition: 'all 150ms ease',
                              '&:hover': {
                                borderColor: isComplete
                                  ? '#8ecaa5'
                                  : isCurrent
                                    ? '#9ebddd'
                                    : '#cbd5e1',
                                backgroundColor: isComplete
                                  ? '#e7f5ec'
                                  : isCurrent
                                    ? '#e8f1fb'
                                    : '#f1f5f9',
                              },
                            }}
                          >
                            {isComplete ? (
                              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                            ) : null}
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                lineHeight: 1,
                              }}
                            >
                              {step.partNumber ? `Part ${step.partNumber}` : step.label}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1.1,
                      py: 0.4,
                      mb: 0.9,
                      borderRadius: 99,
                      backgroundColor: '#eaf0f8',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 800,
                        color: '#52647e',
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        lineHeight: 1.2,
                      }}
                    >
                      Next step
                    </Typography>
                  </Box>
                )}
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 0.5 }}>
                  {currentJourneyStep.key === 'sales-order'
                    ? 'Approve your Sales Order'
                    : currentJourneyStep.key === 'proof'
                      ? 'Approve your artwork proof'
                      : currentJourneyStep.label}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {currentJourneyStep.key === 'sales-order'
                    ? 'Review the order details and colour breakdown above, then approve or request changes.'
                    : currentJourneyStep.key === 'proof' && completeArtworkProofPackagePanel
                      ? 'Review all attached proof files below, then approve the complete artwork package or request changes.'
                      : currentJourneyStep.description}
                </Typography>
              </Box>
            ) : null}
            {(isApproved || approvedNow) ? (
              <>
                {isInactiveSnapshotRevisionView ? (
                  <Typography className={styles.ButtonTypography} style={{ color: '#b26a00', textAlign: 'right' }}>
                    This revision is not the approved version. The sales order was approved on a different version.
                    {' '}
                    <Button
                      variant="text"
                      size="small"
                      onClick={goToLatestSnapshotVersion}
                      sx={{ minWidth: 'auto', p: 0, textTransform: 'none', verticalAlign: 'baseline' }}
                    >
                      View latest approved version
                    </Button>
                    .
                  </Typography>
                ) : !hasMultipleJourneyParts ? (
                  <Typography className={styles.ButtonTypography} style={{ color: 'green' }}>
                    The sales order has been approved.
                  </Typography>
                ) : null}
                {!hasMultipleJourneyParts && (approvedBy.name || approvedBy.email || approvedBy.at) && (
                  <Typography variant="body2" sx={{ color: '#55656d', mt: 0.5 }}>
                    Approved by {approvedByDisplayName || approvedBy.email || 'Unknown approver'}
                    {approvedBy.email ? ` (${approvedBy.email})` : ''}
                    {approvedBy.at ? ` on ${new Date(approvedBy.at).toLocaleString()}` : ''}.
                  </Typography>
                )}
                {balancePaymentNote ? (
                  <Box sx={{ mt: 0.75, textAlign: 'right' }}>
                    {balancePaymentNote}
                  </Box>
                ) : null}

                {orderFirstApprovalFlow && resolvedHasArtworkProofingAttached && !orderFirstProofComplete ? (
                  completeArtworkProofPackagePanel ? (
                    <Box sx={{ mt: 1.25, width: '100%', minWidth: 0 }}>
                      {completeArtworkProofPackagePanel}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 0,
                        p: { xs: 2, md: 2.25 },
                        border: '1px solid #f0c36d',
                        borderRadius: 2,
                        backgroundColor: '#fff8e1',
                        width: '100%',
                        maxWidth: 'none',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: '#8a5b00' }}>Next: approve artwork proof</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, mb: 1, color: '#6b5b1d' }}>
                        Your Sales Order is approved. Please review the artwork proof before continuing to payment.
                      </Typography>
                      <Button variant="contained" size="small" onClick={() => onScrollToArtworkProof?.(normalizedArtworkProofGate.firstBlockingMappingId ?? null)}>
                        Review artwork proofs
                      </Button>
                    </Box>
                  )
                ) : null}

                {orderFirstApprovalFlow && needsUpfront && dueOnSign && orderFirstProofComplete ? (
                  <Box
                    sx={{
                      mt: 0,
                      p: { xs: 2, md: 2.25 },
                      border: isPaid ? '1px solid #c3e6cb' : '1px dashed #93c5fd',
                      borderRadius: 2,
                      background: isPaid ? '#e9f7ef' : '#f0f9ff',
                      width: '100%',
                      maxWidth: 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, mb: 0.5, color: isPaid ? '#155724' : '#1e3a8a' }}>
                      {isPaid ? 'Payment completed' : paymentRequiredLabel}
                    </Typography>
                    {depositDisplay ? (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {isPaid ? paidAmountLabel : amountDueLabel}: <strong>{depositDisplay}</strong>
                      </Typography>
                    ) : null}
                    {(depositInfo?.status || paymentStatusSnap) ? (
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#6b7280' }}>
                        Payment status: <strong>{statusLabel}</strong>
                      </Typography>
                    ) : null}
                    {!isPaid && isCustomerSelectionDeposit ? (
                      renderCustomerSelectionPaymentOptions()
                    ) : !isPaid && paymentLink ? (
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <Button variant="contained" size="small" onClick={openPaymentLink}>Open Checkout</Button>
                        <Button variant="outlined" size="small" onClick={copyPaymentLink}>Copy Checkout Link</Button>
                        <Button variant="text" size="small" onClick={refreshPaymentStatus}>I've paid - refresh</Button>
                      </Stack>
                    ) : !isPaid ? (
                      <Typography variant="body2">Your secure checkout link will be provided by your sales rep shortly.</Typography>
                    ) : null}
                  </Box>
                ) : null}
              </>
            ) : ((isChangeRequested || changeNoted)) ? (
              <Typography className={styles.ButtonTypography} style={{ color: '#d32f2f' }}>
                Your changes have been noted, we will be in contact shortly to discuss next steps
              </Typography>
                ) : (
              <>
                {effectiveArtworkProofGateBlocks ? (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 520,
                      border: '1px solid #f0c36d',
                      backgroundColor: '#fff8e1',
                      borderRadius: 2,
                      p: 2,
                      display: 'grid',
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: '#8a5b00' }}>
                      {artworkProofGateMessage}
                    </Typography>
                    {artworkProofGateSummaryText ? (
                      <Typography variant="body2" sx={{ color: '#6b5b1d' }}>
                        {artworkProofGateSummaryText}
                      </Typography>
                    ) : null}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => onScrollToArtworkProof?.(normalizedArtworkProofGate.firstBlockingMappingId ?? null)}
                      >
                        Review artwork proofs
                      </Button>
                      {/*normalizedArtworkProofGate.firstBlockingProofId ? (
                        <Typography variant="caption" sx={{ alignSelf: 'center', color: '#6b7280' }}>
                          First blocking proof: {normalizedArtworkProofGate.firstBlockingProofId}
                        </Typography>
                      ) : null*/}
                    </Box>
                  </Box>
                ) : orderFirstApprovalFlow ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(260px, 34%)' },
                      gap: { xs: 2, md: 3.5 },
                      width: '100%',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box sx={{ minWidth: 0, pr: { md: 0.5 } }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
                          gap: 1.25,
                          width: '100%',
                          maxWidth: 760,
                        }}
                      >
                        <TextField
                          size="small"
                          label="Your name"
                          value={viewerName || ''}
                          onChange={(e) => setViewerName(e.target.value)}
                          InputProps={{
                            readOnly: isInactiveSnapshotRevisionView,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonOutlineIcon sx={{ color: '#7c8da5', fontSize: 21 }} />
                              </InputAdornment>
                            ),
                          }}
                          disabled={isInactiveSnapshotRevisionView}
                          fullWidth
                          required
                          error={identityAttempted && !identityOkName}
                          helperText={identityAttempted && !identityOkName ? 'Name is required' : ''}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              borderRadius: 2,
                            },
                          }}
                        />
                        <TextField
                          size="small"
                          label="Your email"
                          type="email"
                          value={viewerEmail || ''}
                          onChange={(e) => setViewerEmail(e.target.value)}
                          InputProps={{
                            readOnly: isInactiveSnapshotRevisionView,
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailOutlinedIcon sx={{ color: '#7c8da5', fontSize: 21 }} />
                              </InputAdornment>
                            ),
                          }}
                          disabled={isInactiveSnapshotRevisionView}
                          fullWidth
                          required
                          error={identityAttempted && !identityOkEmail}
                          helperText={identityAttempted && !identityOkEmail ? 'Enter a valid email' : ''}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1.1, color: '#64748b', maxWidth: 760 }}>
                        By approving this Sales Order, you are agreeing to the order details and colour breakdown.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        borderLeft: { xs: 'none', md: '1px solid #dde5ef' },
                        borderTop: { xs: '1px solid #dde5ef', md: 'none' },
                        pt: { xs: 2, md: 0 },
                        pl: { xs: 0, md: 3.5 },
                        display: 'grid',
                        gap: 1.25,
                        alignContent: 'center',
                      }}
                    >
                      {renderAgreeButton && (
                        <Button
                          variant="contained"
                          startIcon={<CheckCircleOutlineIcon />}
                          className={styles.noButton}
                          onClick={onApproveClick}
                          disabled={agreeDisabled}
                          sx={{
                            width: '100%',
                            minHeight: 48,
                            borderRadius: 2,
                            fontWeight: 800,
                            textTransform: 'none',
                            fontSize: 15,
                            background: 'linear-gradient(180deg, #2f9955 0%, #238244 100%)',
                            boxShadow: '0 5px 12px rgba(35, 130, 68, 0.18)',
                            '&:hover': {
                              background: 'linear-gradient(180deg, #2b8f50 0%, #1f763e 100%)',
                              boxShadow: '0 7px 14px rgba(35, 130, 68, 0.22)',
                            },
                            '&.Mui-disabled': {
                              color: '#fff',
                              background: '#a7d7b4',
                              opacity: 0.8,
                            },
                          }}
                        >
                          {agreed ? 'Approved' : 'Approve Order'}
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<EditNoteOutlinedIcon />}
                        className={styles.noButton}
                        onClick={() => {
                          if (!effectiveArtworkProofGateBlocks && !identityOk) {
                            setIdentityAttempted(true);
                            return;
                          }
                          setRcOpen(true);
                        }}
                        disabled={requestDisabled}
                        sx={{
                          width: '100%',
                          minHeight: 48,
                          borderRadius: 2,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: 15,
                          color: '#5b3a12',
                          borderColor: '#e9b54a',
                          backgroundColor: '#fffaf0',
                          '&:hover': {
                            borderColor: '#d89b22',
                            backgroundColor: '#fff4dc',
                          },
                          '&.Mui-disabled': {
                            borderColor: '#ead8ad',
                            color: 'rgba(91,58,18,0.45)',
                            backgroundColor: '#fffaf0',
                          },
                        }}
                      >
                        Request Changes
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, width: '100%', maxWidth: 520, justifyContent: 'flex-end' }}>
                    <TextField
                      size="small"
                      label="Your name"
                      value={viewerName || ''}
                      onChange={(e) => setViewerName(e.target.value)}
                      InputProps={{ readOnly: isInactiveSnapshotRevisionView }}
                      disabled={isInactiveSnapshotRevisionView}
                      fullWidth
                      required
                      error={identityAttempted && !identityOkName}
                      helperText={identityAttempted && !identityOkName ? 'Name is required' : ''}
                    />
                    <TextField
                      size="small"
                      label="Your email"
                      type="email"
                      value={viewerEmail || ''}
                      onChange={(e) => setViewerEmail(e.target.value)}
                      InputProps={{ readOnly: isInactiveSnapshotRevisionView }}
                      disabled={isInactiveSnapshotRevisionView}
                      fullWidth
                      required
                      error={identityAttempted && !identityOkEmail}
                      helperText={identityAttempted && !identityOkEmail ? 'Enter a valid email' : ''}
                    />
                  </Box>
                )}

                {/* primary actions row */}
                {!orderFirstApprovalFlow ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {renderAgreeButton && (
                      <Button
                        variant="contained"
                        className={`${styles.AddCommentButton} ${styles.noButton}`}
                        onClick={onApproveClick}
                        disabled={agreeDisabled}
                        sx={{
                          '&.Mui-disabled': { color: 'white', backgroundColor: 'gray', cursor: 'not-allowed',
                            opacity: 0.3,
                        }
                        }}
                      >
                        <Typography className={styles.ButtonTypography}>
                          {agreed ? 'Approved' : 'Agree'}
                        </Typography>
                      </Button>
                    )}

                    {!resolvedHasArtworkProofingAttached ? (
                      <Button
                        variant="contained"
                        className={styles.noButton}
                        onClick={() => {
                          if (!effectiveArtworkProofGateBlocks && !identityOk) {
                            setIdentityAttempted(true);
                            return;
                          }
                          setRcOpen(true);
                        }}
                        disabled={requestDisabled}
                        sx={{
                          backgroundColor: '#ffd25580',
                          color: '#000',
                          border: '1px solid rgba(0,0,0,0.15)',
                          '&:hover': { backgroundColor: '#ffd255' },
                          '&:focus-visible': { outline: '2px solid #000' },
                          '& .MuiTypography-root': { color: '#000' },
                          '&.Mui-disabled': {
                            backgroundColor: '#ffd25580',
                            color: 'rgba(0,0,0,0.38)',
                            cursor: 'not-allowed',
                            opacity: 0.3,
                          }
                        }}
                      >
                        <Typography className={styles.ButtonTypography} sx={{ color: '#000' }}>
                          Request Changes
                        </Typography>
                      </Button>
                    ) : null}
                  </Box>
                ) : null}

                {/* PAYMENT GATE — Agreement + upfront payment required + due on sign */}
                {!orderFirstApprovalFlow && isAgreement && needsUpfront && dueOnSign && artworkProofGateAllowsProceeding && !isInactiveSnapshotRevisionView && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 2,
                      border: isPaid ? '1px solid #c3e6cb' : '1px dashed #93c5fd',
                      borderRadius: 2,
                      background: isPaid ? '#e9f7ef' : '#f0f9ff',
                      width: '100%',
                      maxWidth: 520
                    }}
                  >
                    {isPaid ? (
                      <>
                        <Typography sx={{ fontWeight: 700, mb: 0.5, color: '#155724' }}>
                          ✅ Approved & paid
                        </Typography>
                        {depositDisplay && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {paidAmountLabel}: <strong>{depositDisplay}</strong>
                        </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                          {paymentRequiredLabel} to approve
                        </Typography>
                        {depositDisplay && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {amountDueLabel}: <strong>{depositDisplay}</strong>
                          </Typography>
                        )}
                      </>
                    )}

                    {(depositInfo?.status || paymentStatusSnap) && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#6b7280' }}>
                        Payment status: <strong>{statusLabel}</strong>
                      </Typography>
                    )}
                    {!(isApproved || approvedNow) ? balancePaymentNote : null}
                    {isPaid && (
                      <Typography variant="body2" sx={{ mb: 1, color: '#155724' }}>
                        ✅ Payment completed.
                        {stripeDashboardUrl ? (
                          <>
                            {' '}
                            <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
                              View on Stripe
                            </a>
                          </>
                        ) : null}
                      </Typography>
                    )}

                    {/* Customer-selection deposits choose payment first; legacy checkout UI remains for immediate Stripe links. */}
                    {!isPaid && isCustomerSelectionDeposit ? (
                      renderCustomerSelectionPaymentOptions()
                    ) : paymentLink && !isPaid ? (
                      <>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={openPaymentLink}
                          >
                            Open Checkout
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={copyPaymentLink}
                          >
                            Copy Checkout Link
                          </Button>
                          <Button
                            variant="text"
                            size="small"
                            onClick={refreshPaymentStatus}
                          >
                            I’ve paid — refresh
                          </Button>
                        </Stack>
                        <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                          Payment link:{" "}
                          <a
                            href={paymentLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={async (e) => {
                              e.preventDefault();
                              await openPaymentLink();
                            }}
                          >
                            Click here to view your payment link
                          </a>
                        </Typography>
                        {isLinkExpired && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#b45309', mb: 0.5 }}>
                              Payment link expired — generate a new link to continue.
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={renewPaymentLink}
                              disabled={renewingLink}
                            >
                              {renewingLink ? 'Generating...' : 'Generate new link'}
                            </Button>
                            {renewError ? (
                              <Typography variant="caption" sx={{ display: 'block', color: '#b91c1c', mt: 0.5 }}>
                                {renewError}
                              </Typography>
                            ) : null}
                          </Box>
                        )}
                        {!isPaid && (
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            After payment succeeds, the <strong>Agree</strong> button will become available.
                          </Typography>
                        )}
                      </>
                    ) : !isPaid ? (
                      <>
                        {isLinkExpired ? (
                          <Box>
                            <Typography variant="body2" sx={{ color: '#b45309', mb: 0.75 }}>
                              Payment link expired — generate a new link to continue.
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={renewPaymentLink}
                              disabled={renewingLink}
                            >
                              {renewingLink ? 'Generating...' : 'Generate new link'}
                            </Button>
                            {renewError ? (
                              <Typography variant="caption" sx={{ display: 'block', color: '#b91c1c', mt: 0.5 }}>
                                {renewError}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            Your secure checkout link will be provided by your sales rep shortly.
                          </Typography>
                        )}
                      </>
                    ) : null}
                  </Box>
                )}

                {isApproved && needsUpfront && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 2,
                      border: '1px solid #c3e6cb',
                      borderRadius: 2,
                      background: '#e9f7ef',
                      width: '100%',
                      maxWidth: 520
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 0.5, color: '#155724' }}>
                      Payment completed
                    </Typography>
                    {depositDisplay && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {paidAmountLabel}: <strong>{depositDisplay}</strong>
                      </Typography>
                    )}
                    {(depositInfo?.status || paymentStatusSnap) && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280' }}>
                        Payment status: <strong>{statusLabel}</strong>
                      </Typography>
                    )}
                    {!(isApproved || approvedNow) ? balancePaymentNote : null}
                    {stripeDashboardUrl ? (
                      <Typography variant="body2">
                        <a href={stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
                          View paid invoice on Stripe
                        </a>
                      </Typography>
                    ) : null}
                    {depositInfo?.id ? (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <a href={`/generated-invoice/${depositInfo.id}`} target="_blank" rel="noopener noreferrer">
                          View generated invoice
                        </a>
                      </Typography>
                    ) : null}
                  </Box>
                )}

                {(!effectiveArtworkProofGateBlocks && !identityOk && identityAttempted) ? (
                  <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                    Please enter your name and a valid email to continue.
                  </Typography>
                ) : (!orderFirstApprovalFlow && !effectiveArtworkProofGateBlocks && identityMissing) ? (
                  <Typography variant="caption" color="text.secondary">
                    (Adding your name/email helps us track who requested the change or approved the order.)
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        )}
      </Grid>

      {/* Request Changes dialog */}
      <Dialog className={styles.printHidden} open={rcOpen} onClose={() => setRcOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request changes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="What would you like changed?"
            placeholder="Share as much detail as you can…"
            fullWidth
            multiline
            minRows={4}
            value={rcComment}
            onChange={(e) => setRcComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRcOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={onRequestChangeSend}
            disabled={submitting || !rcComment.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {canViewDebugPayload && (
        <Grid className={styles.printHidden} sx={{ mt: 2 }}>
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>
                Administrator Payload Debug
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  maxHeight: 360,
                  overflow: 'auto',
                  borderRadius: 1,
                  backgroundColor: '#0f172a',
                  color: '#e2e8f0',
                  fontSize: 12,
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {debugPayload}
              </Box>
              <Box
                component="pre"
                sx={{
                  mt: 1.5,
                  m: 0,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#111827',
                  color: '#fef3c7',
                  fontSize: 12,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
{`DISPLAYED SUBTOTAL MATH:
${debugMath.subtotalExpr}
= ${debugMath.subtotalRaw.toFixed(2)}

TAX:
${!isEstimateView && debugMath.taxGroupLines.length
  ? debugMath.taxGroupLines.join('\n')
  : `${debugMath.subtotalRaw.toFixed(2)} * ${debugMath.taxRateNum.toFixed(4)} = ${debugMath.taxRaw.toFixed(2)}`}

TOTAL:
${(isEstimateView ? debugMath.subtotalRaw : subtotal).toFixed(2)} + ${debugMath.taxRaw.toFixed(2)} = ${debugMath.totalRaw.toFixed(2)}

ITEM LINES:
${debugMath.subtotalPieces.map((p) => `${p.label} = ${p.amount.toFixed(2)}`).join('\n')}`}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      {/* Terms */}
      {termsHtml?.trim() ? (
        <Grid className={styles.termsContainer}>
          <Box dangerouslySetInnerHTML={{ __html: termsHtml }} />
        </Grid>
      ) : (
        <Grid className={styles.termsContainer}>
          <Box className={styles.termsHeading}><Typography className={styles.Titleheading}>TERMS AND CONDITIONS</Typography></Box>
          <Box><Typography className={styles.heading}>THANK YOU FOR THE OPPORTUNITY TO PROVIDE THIS PROMOTIONAL SALES ORDER</Typography></Box>
          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              PRICING: does not include freight (FOB manufacturer) or applicable taxes unless otherwise noted.
              Typical production times are 3–4 weeks, with exceptions. On timelines of 10 days or less, rush
              charges may apply.
            </Typography>
          </Box>
          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              ARTWORK: must be submitted according to supplier specifications (vector files in Adobe Illustrator,
              all fonts converted to outlines). If Elite Promotional Marketing is creating or editing your artwork,
              artwork charges are additional. We provide in-house graphic services at an hourly rate of $80.00,
              broken down into 15 minute increments of $20.00.
            </Typography>
          </Box>
          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              TERMS: First time orders and orders placed without payment terms require 100% pre-payment. All
              orders over $20,000 require a 50% deposit. We accept all major credit cards like Visa, M/C, American
              Express, cheques, and cash.
            </Typography>
          </Box>
          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              While it is understood that the products and prices herein may be part of a competitive bid, this
              information is our intellectual property; it is to be kept confidential and should not be given to
              our competitors for comparison.
            </Typography>
          </Box>
          <Box><Typography className={styles.heading}>ALL PRICING SUBJECT TO CHANGE.</Typography></Box>
          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              If you require a freight estimate, please provide quantity (freight estimates can take up to 24–48 hours).
            </Typography>
          </Box>
        </Grid>
      )}


    </>
  );
};

export default Footer;
